import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import styles from './QualityCheckView.module.css';
import OperationsHub from '../../components/OperationsHub';
import { ManualEntryDashboard } from '../manual-entry';
import { EnrichmentVerificationView } from '../enrichment-verification';

export type QualityCheckTab = 'operations' | 'manual' | 'verification';

export interface QualityCheckViewProps {
  initialTab?: QualityCheckTab;
}

interface TabConfigItem {
  id: QualityCheckTab;
  label: string;
  description: string;
}

const TAB_CONFIG: TabConfigItem[] = [
  {
    id: 'operations',
    label: 'Ops Dashboard',
    description: 'Monitor crawls, ingestion jobs, and AI-assisted quality queues.',
  },
  {
    id: 'manual',
    label: 'Manual Entry',
    description: 'Capture structured updates, validate content, and lock records after review.',
  },
  {
    id: 'verification',
    label: 'AI Verification Queue',
    description: 'Triage low-confidence fields and feed corrections back to the learning loop.',
  },
];

const QualityCheckView: React.FC<QualityCheckViewProps> = ({ initialTab = 'operations' }) => {
  const [activeTab, setActiveTab] = useState<QualityCheckTab>(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const activeMeta = useMemo(() => TAB_CONFIG.find((tab) => tab.id === activeTab) ?? TAB_CONFIG[0], [activeTab]);

  const renderActivePanel = () => {
    switch (activeTab) {
      case 'manual':
        return <ManualEntryDashboard />;
      case 'verification':
        return <EnrichmentVerificationView />;
      case 'operations':
      default:
        return <OperationsHub />;
    }
  };

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>Quality Check</h2>
          <p className={styles.subtitle}>
            Unified workflow for keeping award program data trustworthy. Review automated jobs, apply manual fixes,
            and confirm AI-extracted fields without bouncing between screens.
          </p>
        </div>
        <div className={styles.tabBar} role="tablist" aria-label="Quality Check workflows">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={clsx(styles.tabButton, activeTab === tab.id && styles.tabButtonActive)}
              onClick={() => setActiveTab(tab.id)}
              aria-selected={activeTab === tab.id}
              role="tab"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <div className={styles.panel}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{activeMeta.label}</h3>
          <span className={styles.sectionHint}>{activeMeta.description}</span>
        </div>
        {renderActivePanel()}
        {activeTab === 'verification' ? (
          <p className={styles.inlineNote}>
            Approve, correct, or escalate low-confidence findings. Every decision feeds the training buffer so the
            ingestion agents improve on the next crawl cycle.
          </p>
        ) : null}
        {activeTab === 'manual' ? (
          <p className={styles.inlineNote}>
            Use structured panels to capture categories, fees, and deadlines. Validate with Supabase RPCs before saving
            so downstream quality scores stay aligned.
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default QualityCheckView;
